import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.css";
import { isMobile } from "react-device-detect";
import {
  Alert,
  Button,
  Card,
  Col,
  List,
  Modal,
  Popconfirm,
  Row,
  Typography,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  DocumentTitle,
  NetworkError,
  QuantitySelector,
} from "../../components";
import I18n from "../../i18n";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import {
  decreaseCartItemCount,
  increaseCartItemCount,
  loadCartItems,
  removeFromCart,
} from "../../../application/cart-slice";
import { CartItem } from "../../../domain";
import { Link, useHistory } from "react-router-dom";
import { useQuery } from "../../hooks";

export default function Cart() {
  const query = useQuery();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { itemsLoading, items } = useAppSelector((state) => state.cart);
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(loadCartItems());
  }, [stableDispatch]);

  const increaseItemCount = (cartItem: CartItem) => {
    dispatch(
      increaseCartItemCount({
        attributeId: cartItem.attributeId,
        productId: cartItem.productId,
      })
    );
  };

  const decreaseItemCount = (cartItem: CartItem) => {
    dispatch(
      decreaseCartItemCount({
        attributeId: cartItem.attributeId,
        productId: cartItem.productId,
      })
    );
  };

  const deleteItem = (cartItem: CartItem) => {
    dispatch(
      removeFromCart({
        productId: cartItem.productId,
        attributeId: cartItem.attributeId,
      })
    );
  };

  const calculateTotalPriceWithoutOff = (cartItems: CartItem[]) => {
    let sum = 0;
    cartItems.forEach((item) => {
      sum +=
        item.product.attributes.find(
          (attribute) => attribute.id.toString() === item.attributeId
        )!.price * item.count;
    });
    return sum;
  };

  const calculateOffAmount = (cartItems: CartItem[]) => {
    let sum = 0;
    cartItems.forEach((item) => {
      const attribute = item.product.attributes.find(
        (attribute) => attribute.id.toString() === item.attributeId
      );
      if (attribute?.hasOff())
        sum += (attribute!.price - attribute!.offPrice) * item.count;
    });
    return sum;
  };

  const calculateOffPercent = (cartItems: CartItem[]) => {
    return Math.ceil(
      (calculateOffAmount(items) / calculateTotalPriceWithoutOff(items)) * 100
    );
  };

  const calculateFinalPrice = (cartItems: CartItem[]) =>
    calculateTotalPriceWithoutOff(items) - calculateOffAmount(items);

  const [cartHasErrors, setCartHasErrors] = useState(false);

  const [modal, contextHolder] = Modal.useModal();

  const showEditCartAlert = () => {
    modal.warning({
      content: "جهت ادامه سبد خرید خود را اصلاح کنید.",
      direction: "rtl",
      centered: true,
    });
  };

  const stableShowEditCartAlert = useCallback(showEditCartAlert, []);
  const isInvalid = query.get("invalid") === "true";
  useEffect(() => {
    if (isInvalid === true) {
      setCartHasErrors(true);
      stableShowEditCartAlert();
    }
  }, [isInvalid, stableShowEditCartAlert]);

  const continuePurchase = () => {
    let hasError = false;
    for (let index = 0; index < items.length; index++) {
      const element = items[index];
      const selectedAttribute = element.product.attributes.find(
        (attribute) => attribute.id === element.attributeId
      );
      if (element.count > selectedAttribute!.count) {
        showEditCartAlert();
        hasError = true;
        break;
      }
    }
    setCartHasErrors(hasError);
    if (hasError === false) {
      if (isAuthenticated === "authenticated") history.push("/select-address");
      else history.push("/login-register?back=/select-address");
    }
  };

  const renderItem = (item: CartItem, index: number) => {
    const selectedAttribute = item.product.attributes.find(
      (attribute) => attribute.id === item.attributeId
    );
    return (
      <List.Item className="cart-item">
        <Col>
          <Row>
            <Link
              to={`/product/${item.productId}/${encodeURIComponent(
                item.product.title
              )}`}
            >
              <img
                src={item.product.images[0]}
                alt={item.product.title}
                width={100}
              />
            </Link>
            <div className="cart-item-info">
              <Row justify="space-between" align="middle">
                <Typography.Title level={5}>
                  {item.product.title}
                </Typography.Title>
                <Popconfirm
                  title={I18n.cart.deleteProductConfirm}
                  placement="rightTop"
                  onConfirm={() => deleteItem(item)}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<DeleteOutlined />}
                    danger
                  >
                    {I18n.general.delete}
                  </Button>
                </Popconfirm>
              </Row>
              <Typography.Text>
                سایز{" "}
                <span className="cart-item-size">
                  {selectedAttribute!.size}
                </span>{" "}
                به رنگ{" "}
                <span className="cart-item-color">
                  {selectedAttribute!.color.title}
                </span>
              </Typography.Text>
              <Row
                justify="space-between"
                align="middle"
                className="cart-item-quantity-selector"
              >
                <QuantitySelector
                  quantity={item.count}
                  onIncrease={() => increaseItemCount(item)}
                  onDecrease={() => decreaseItemCount(item)}
                  increaseDisabled={selectedAttribute!.count <= item.count}
                />
                <div>
                  {selectedAttribute?.hasOff() ? (
                    <Row justify="end">
                      <Typography.Text className="cart-item-off-price">
                        {(selectedAttribute!.price * item.count).toLocaleString(
                          "fa"
                        )}{" "}
                        تومان
                      </Typography.Text>
                    </Row>
                  ) : (
                    <Fragment />
                  )}
                  <Row justify="end">
                    <Typography.Text>
                      {(
                        (selectedAttribute!.hasOff()
                          ? selectedAttribute!.offPrice
                          : selectedAttribute!.price) * item.count
                      ).toLocaleString("fa")}{" "}
                      تومان
                    </Typography.Text>
                  </Row>
                </div>
              </Row>
            </div>
          </Row>

          {cartHasErrors && selectedAttribute!.count < item.count ? (
            <Alert
              style={{ marginTop: 8 }}
              message={
                selectedAttribute!.count === 0
                  ? "این محصول با این رنگ و سایز در حال حاضر ناموجود میباشد."
                  : `در حال حاضر تنها ${selectedAttribute?.count} عدد از این محصول با این رنگ و سایز موجود میباشد. لطفا تعداد مورد نظر را اصلاح نمایید.`
              }
              type="error"
              showIcon
            />
          ) : (
            <Fragment />
          )}
        </Col>
      </List.Item>
    );
  };

  const isLoading = itemsLoading === "pending";
  const isEmpty = items.length === 0;

  if (itemsLoading === "failed") return <NetworkError />;
  return (
    <DocumentTitle title={I18n.cart.title}>
      {contextHolder}
      <Col lg={isEmpty ? undefined : 17} md={isEmpty ? undefined : 16}>
        <Card
          className={isMobile ? "cart-card-mobile" : "cart-card-desktop"}
          bodyStyle={{ padding: 0 }}
        >
          <div className="cart-title">{I18n.cart.title}</div>
          <List
            dataSource={items}
            loading={isLoading}
            renderItem={renderItem}
          />
        </Card>
      </Col>
      {isEmpty ? (
        <Fragment />
      ) : (
        <Col lg={7} md={8}>
          <Card
            className={
              isMobile
                ? "purchase-info-card-mobile"
                : "purchase-info-card-desktop"
            }
            bodyStyle={{ padding: 0 }}
          >
            <Row justify="space-between" className="prices-box-text">
              <Typography.Text>قیمت کالاها</Typography.Text>
              <Typography.Text>
                {calculateTotalPriceWithoutOff(items).toLocaleString()} تومان
              </Typography.Text>
            </Row>
            {calculateOffAmount(items) === 0 ? (
              <Fragment />
            ) : (
              <Row justify="space-between" className="prices-box-text">
                <Typography.Text>میزان تخفیف</Typography.Text>
                <Typography.Text>
                  ({`${calculateOffPercent(items)}%`}){" "}
                  {calculateOffAmount(items).toLocaleString()} تومان
                </Typography.Text>
              </Row>
            )}

            <Row
              justify="space-between"
              className="prices-box-text"
              style={{ fontWeight: "bold" }}
            >
              <Typography.Text>جمع کل</Typography.Text>
              <Typography.Text>
                {calculateFinalPrice(items).toLocaleString()} تومان
              </Typography.Text>
            </Row>
            {/* <div className="cart-title">
              جمع کل:‌ {calculateFinalPrice(items).toLocaleString()} تومان
            </div> */}
            <div className="cart-submit-button">
              <Button
                type="primary"
                size="large"
                block
                onClick={continuePurchase}
                // loading={isVerifying === "pending"}
              >
                ادامه فرآیند خرید
              </Button>
            </div>
          </Card>
        </Col>
      )}
    </DocumentTitle>
  );
}
